import { useState } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { Button } from 'shared-components';
import Modal from './Modal';
import ModalFrame from './ModalFrame';

export type ButtonConfig = Omit<ComponentProps<typeof Button>, 'onClick' | 'children'> & {
	text?: string;
	children?: ReactNode;
};

export interface ConfirmPromptProps {
	open: boolean;
	onClose: () => void;
	title?: string;
	text: string | React.ReactNode;
	icon?: string;
	confirmButton?: ButtonConfig;
	cancelButton?: ButtonConfig;
	onConfirm: () => void | Promise<void>;
	onCancel?: () => void;
}

export default function ConfirmPrompt(props: ConfirmPromptProps) {
	const {
		open,
		onClose,
		title = 'Confirm',
		text,
		icon,
		confirmButton = {},
		cancelButton = {},
		onConfirm,
		onCancel,
	} = props;

	const [isProcessing, setIsProcessing] = useState(false);

	const handleConfirm = async () => {
		setIsProcessing(true);
		try {
			await onConfirm();
			onClose();
		} catch (error) {
			console.error('Error during confirmation:', error);
		} finally {
			setIsProcessing(false);
		}
	};

	const handleCancel = () => {
		if (onCancel) {
			onCancel();
		}
		onClose();
	};

	const {
		text: cancelText,
		children: cancelChildren,
		disabled: cancelDisabled,
		icon: cancelIcon,
		...cancelButtonProps
	} = cancelButton;

	const {
		text: confirmText,
		children: confirmChildren,
		disabled: confirmDisabled,
		icon: confirmIcon,
		...confirmButtonProps
	} = confirmButton;

	const cancelContent = cancelChildren ?? cancelText ?? 'Cancel';
	const confirmContent = confirmChildren ?? confirmText ?? 'Confirm';

	const resolvedCancelProps: ComponentProps<typeof Button> = {
		color: 'secondary',
		size: 'md',
		...cancelButtonProps,
	};

	const resolvedConfirmProps: ComponentProps<typeof Button> = {
		color: 'primary',
		size: 'md',
		...confirmButtonProps,
	};

	return (
		<Modal open={open} onClose={onClose}>
			<ModalFrame size="sm" title={title} padding="1.5rem">
				<div className="confirm-prompt">
					{icon && (
						<div className="icon">
							<i className={`${icon} fa-3x`} />
						</div>
					)}
					<div className="text">{text}</div>
					<div className="actions">
						<Button
							{...resolvedCancelProps}
							icon={cancelIcon}
							onClick={handleCancel}
							disabled={isProcessing || cancelDisabled}
						>
							{cancelContent}
						</Button>
						<Button
							{...resolvedConfirmProps}
							icon={isProcessing ? 'fa fa-spinner fa-spin' : confirmIcon}
							onClick={handleConfirm}
							disabled={isProcessing || confirmDisabled}
						>
							{confirmContent}
						</Button>
					</div>
				</div>
			</ModalFrame>
		</Modal>
	);
}
